import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Badge,
  Skeleton,
  Button,
} from '@bloomcode/ui';
import { useSearchPostsQuery, type SearchPostsQuery } from '@bloomcode/graphql';
import { formatDate } from '@bloomcode/utils';
import { useCookieSession } from '@bloomcode/auth';
import { useDebounce } from '@bloomcode/hooks';

type PostItem = NonNullable<SearchPostsQuery['searchPosts']>['data'][number];

export default function SearchPage() {
  const { session } = useCookieSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const tagFilter = searchParams.get('tag') || '';

  const [searchInput, setSearchInput] = useState(initialQuery);
  const debouncedQuery = useDebounce(searchInput, 300);

  useEffect(() => {
    if (debouncedQuery !== initialQuery) {
      const params = new URLSearchParams();
      if (debouncedQuery) params.set('q', debouncedQuery);
      if (tagFilter) params.set('tag', tagFilter);
      setSearchParams(params);
    }
  }, [debouncedQuery, tagFilter, initialQuery, setSearchParams]);

  const searchTerm = debouncedQuery || tagFilter;

  const { data, loading, error, refetch } = useSearchPostsQuery({
    variables: {
      query: searchTerm,
      pagination: { page: 1, limit: 50 },
    },
    skip: !searchTerm || !session,
    fetchPolicy: 'no-cache',
  });

  const posts: PostItem[] = data?.searchPosts?.data ?? [];

  const clearFilters = () => {
    setSearchInput('');
    setSearchParams({});
  };

  return (
    <>
      <Helmet>
        <title>Search{debouncedQuery ? `: ${debouncedQuery}` : ''} | Bloomcode CMS</title>
        <meta name="description" content="Search content" />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-4">Search</h1>
          <div className="flex gap-4 items-center">
            <Input
              placeholder="Search content..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="max-w-md"
            />
            {(debouncedQuery || tagFilter) && (
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </div>
          {tagFilter && (
            <div className="mt-4">
              <span className="text-muted-foreground">Filtering by tag: </span>
              <Badge variant="secondary">#{tagFilter}</Badge>
            </div>
          )}
        </div>

        {error ? (
          <div className="text-center py-12">
            <p className="text-destructive mb-4">Search failed: {error.message}</p>
            <button onClick={() => refetch()} className="text-primary underline">
              Retry
            </button>
          </div>
        ) : loading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-6 w-3/4" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-4 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : posts.length > 0 ? (
          <>
            <p className="text-muted-foreground mb-4">
              Found {posts.length} result{posts.length !== 1 ? 's' : ''}
            </p>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {posts.map((post) => (
                <Link key={post.id} to={`/content/${post.slug}`}>
                  <Card className="h-full hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-center justify-between mb-2">
                        {post.categories?.[0] && (
                          <Badge variant="secondary">{post.categories[0].name}</Badge>
                        )}
                        <span className="text-sm text-muted-foreground">
                          {formatDate(post.published_at || post.created_at)}
                        </span>
                      </div>
                      <CardTitle className="line-clamp-2">{post.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {post.excerpt && (
                        <p className="text-muted-foreground line-clamp-3">{post.excerpt}</p>
                      )}
                      {post.tags && post.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-4">
                          {post.tags.slice(0, 3).map((tag) => (
                            <Badge key={tag.id} variant="outline" className="text-xs">
                              #{tag.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              {searchTerm
                ? 'No content found matching your search.'
                : 'Enter a search term to find content.'}
            </p>
          </div>
        )}
      </div>
    </>
  );
}
